import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { apiClient } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  Package,
  CheckCircle2,
  Truck,
  Clock,
  ChevronRight,
  ArrowLeft,
  ShoppingBag,
  Sparkles,
  MapPin,
  Mail,
  FastForward,
} from "lucide-react";

const STATUS_FLOW = [
  { key: "paid", label: "Paid", icon: CheckCircle2 },
  { key: "processing", label: "Processing", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

const statusBadge = (status) => {
  const map = {
    paid: { bg: "bg-blue-500/10 text-blue-700 border-blue-500/20", label: "Paid" },
    processing: { bg: "bg-amber-500/10 text-amber-700 border-amber-500/20", label: "Processing" },
    shipped: { bg: "bg-indigo-500/10 text-indigo-700 border-indigo-500/20", label: "Shipped" },
    delivered: { bg: "bg-green-500/10 text-green-700 border-green-500/20", label: "Delivered" },
    cancelled: { bg: "bg-destructive/10 text-destructive border-destructive/20", label: "Cancelled" },
  };
  return map[status] || map.paid;
};

const formatKES = (n) => `KES ${Number(n || 0).toLocaleString()}`;

const Orders = () => {
  const { orderId } = useParams();

  if (orderId) return <OrderDetail orderId={orderId} />;
  return <OrdersList />;
};

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get("/orders");
        setOrders(res.data || []);
      } catch (e) {
        toast.error("Failed to load orders");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8" data-testid="orders-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">My Orders</h1>
          </div>
          <p className="text-muted-foreground">
            Track your purchases and order history.
          </p>
        </motion.div>

        {orders.length === 0 ? (
          <Card data-testid="empty-orders">
            <CardContent className="text-center py-16">
              <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-heading font-semibold text-lg mb-2">No orders yet</h3>
              <p className="text-muted-foreground mb-6">
                Start exploring BE Wear, BE Fit and BE Life products.
              </p>
              <Button onClick={() => navigate("/")} data-testid="shop-now-btn">
                Shop Now
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order, index) => {
              const badge = statusBadge(order.status);
              const itemCount = order.items?.reduce((s, i) => s + (i.quantity || 1), 0) || 0;
              return (
                <motion.div
                  key={order.order_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className="hover:border-primary transition-colors cursor-pointer"
                    onClick={() => navigate(`/orders/${order.order_id}`)}
                    data-testid={`order-row-${order.order_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                            <Package className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs text-muted-foreground">
                                #{order.order_id.slice(-10).toUpperCase()}
                              </span>
                              <Badge variant="outline" className={badge.bg}>
                                {badge.label}
                              </Badge>
                            </div>
                            <p className="font-medium">
                              {itemCount} item{itemCount !== 1 ? "s" : ""}
                              {order.points_earned ? ` • +${order.points_earned} pts` : ""}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(order.created_at).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="font-heading font-bold text-xl">
                              {formatKES(order.total_amount)}
                            </div>
                            <div className="text-xs text-muted-foreground">Total</div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

const OrderDetail = ({ orderId }) => {
  const [order, setOrder] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const navigate = useNavigate();

  const loadOrder = async () => {
    const [orderRes, emailsRes] = await Promise.all([
      apiClient.get(`/orders/${orderId}`),
      apiClient.get(`/orders/${orderId}/emails`).catch(() => ({ data: [] })),
    ]);
    setOrder(orderRes.data);
    setEmails(emailsRes.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadOrder();
      } catch (e) {
        toast.error("Order not found");
        navigate("/orders");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const advanceStatus = async () => {
    setAdvancing(true);
    try {
      const res = await apiClient.post(`/orders/${orderId}/advance`);
      toast.success(`Status updated to ${res.data.status}. Email sent (simulated).`);
      await loadOrder();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not advance status");
    } finally {
      setAdvancing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!order) return null;

  const badge = statusBadge(order.status);
  const currentIdx = STATUS_FLOW.findIndex((s) => s.key === order.status);
  const isCancelled = order.status === "cancelled";

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8" data-testid="order-detail-page">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/orders")}
          className="mb-4"
          data-testid="back-to-orders"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to orders
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="font-heading font-bold text-3xl md:text-4xl">
              Order #{order.order_id.slice(-10).toUpperCase()}
            </h1>
            <Badge variant="outline" className={badge.bg}>
              {badge.label}
            </Badge>
          </div>
          <p className="text-muted-foreground">
            Placed on{" "}
            {new Date(order.created_at).toLocaleString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Tracking */}
            <Card data-testid="tracking-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Truck className="w-5 h-5 text-primary" />
                  Order Tracking
                </CardTitle>
                <CardDescription>
                  {isCancelled
                    ? "This order has been cancelled."
                    : order.status === "delivered"
                    ? "Order delivered successfully."
                    : "Your order is on its way."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isCancelled ? (
                  <div className="p-4 bg-destructive/10 text-destructive rounded-xl">
                    Order cancelled.
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-border" />
                    <div className="space-y-6">
                      {STATUS_FLOW.map((step, idx) => {
                        const Icon = step.icon;
                        const reached = idx <= currentIdx;
                        const active = idx === currentIdx;
                        return (
                          <div key={step.key} className="flex items-start gap-4 relative">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center z-10 ${
                                reached
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 pt-2">
                              <div className="flex items-center gap-2">
                                <p
                                  className={`font-medium ${
                                    reached ? "" : "text-muted-foreground"
                                  }`}
                                >
                                  {step.label}
                                </p>
                                {active && (
                                  <Badge className="bg-primary/10 text-primary border-primary/20">
                                    Current
                                  </Badge>
                                )}
                              </div>
                              {idx === 0 && (
                                <p className="text-sm text-muted-foreground">
                                  {new Date(order.created_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {order.estimated_delivery && !isCancelled && order.status !== "delivered" && (
                  <div className="mt-6 flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">
                      Estimated delivery:{" "}
                      <span className="font-medium">
                        {new Date(order.estimated_delivery).toLocaleDateString()}
                      </span>
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Items */}
            <Card data-testid="items-card">
              <CardHeader>
                <CardTitle className="font-heading">Items ({order.items?.length || 0})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {order.items?.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex gap-4 items-center"
                    data-testid={`order-item-${idx}`}
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      {item.product?.image_url ? (
                        <img
                          src={item.product.image_url}
                          alt={item.product?.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/product/${item.product_id}`}
                        className="font-medium hover:text-primary truncate block"
                      >
                        {item.product?.name || item.product_id}
                      </Link>
                      <p className="text-sm text-muted-foreground">
                        Qty: {item.quantity}
                        {item.size ? ` • Size ${item.size}` : ""}
                        {item.color ? ` • ${item.color}` : ""}
                      </p>
                    </div>
                    <div className="font-heading font-bold">
                      {formatKES((item.product?.price || 0) * item.quantity)}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Summary */}
          <div className="space-y-6">
            <Card data-testid="summary-card">
              <CardHeader>
                <CardTitle className="font-heading">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatKES(order.subtotal ?? order.total_amount)}</span>
                </div>
                {order.shipping_fee != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    <span>{formatKES(order.shipping_fee)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-heading font-bold text-lg">
                  <span>Total</span>
                  <span>{formatKES(order.total_amount)}</span>
                </div>
                {order.points_earned ? (
                  <div className="flex items-center gap-2 p-3 bg-accent/10 rounded-lg mt-3">
                    <Sparkles className="w-4 h-4 text-accent" />
                    <span className="text-sm">
                      You earned{" "}
                      <span className="font-bold">+{order.points_earned} BE Points</span>
                    </span>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            {order.shipping_address ? (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    Shipping
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">
                  {order.shipping_address}
                </CardContent>
              </Card>
            ) : null}

            {/* Email Activity */}
            <Card data-testid="email-activity-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  Email Notifications
                </CardTitle>
                <CardDescription>
                  {emails.length === 0
                    ? "No emails sent for this order yet."
                    : `${emails.length} email${emails.length === 1 ? "" : "s"} sent${
                        emails[0]?.simulated ? " (simulated)" : ""
                      }`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {emails.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    You'll receive a confirmation email at checkout and updates as your
                    order progresses.
                  </p>
                ) : (
                  emails.map((e) => (
                    <div
                      key={e.email_id}
                      className="p-3 bg-muted/40 rounded-lg"
                      data-testid={`email-${e.email_id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <Badge variant="outline" className="capitalize">
                          {e.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(e.created_at).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <p className="text-sm font-medium truncate">{e.subject}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        To: {e.to}
                      </p>
                    </div>
                  ))
                )}
                {!isCancelled && order.status !== "delivered" && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={advanceStatus}
                    disabled={advancing}
                    data-testid="simulate-next-status-btn"
                  >
                    <FastForward className="w-4 h-4 mr-2" />
                    {advancing ? "Simulating..." : "Simulate next status + email"}
                  </Button>
                )}
              </CardContent>
            </Card>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate("/")}
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Orders;
